/**
 * Operational Validation Dashboard
 * 
 * The FAA pre-flight checklist for home care agencies.
 * This is not a software test — it's proof that the agency can successfully
 * perform daily work using Aligned.
 * 
 * Every certification answers: "Can A Touch of Angels perform this activity
 * today without leaving Aligned?"
 * 
 * Confidence Levels:
 * - 🟢 Certified — Successfully used multiple times in real operations
 * - 🟡 Validated — Passed testing but limited real-world use
 * - 🔵 Configured — Setup complete but not yet exercised
 * - ⚪ Not Started — Awaiting validation
 */
import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  CheckCircle, AlertTriangle, Clock, XCircle, Building, Heart, User,
  Calendar, DollarSign, Sparkles, Shield, FileText, ChevronDown, ChevronUp,
  Loader2, RefreshCw, Check, X, ArrowRight, AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";

// Confidence level configurations
const CONFIDENCE_CONFIG = {
  certified: { 
    icon: "🟢", 
    label: "Certified", 
    description: "Successfully used multiple times in real operations",
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  validated: { 
    icon: "🟡", 
    label: "Validated", 
    description: "Passed testing but limited real-world use",
    color: "bg-amber-100 text-amber-800 border-amber-200",
  },
  configured: { 
    icon: "🔵", 
    label: "Configured", 
    description: "Setup complete but not yet exercised",
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  not_started: { 
    icon: "⚪", 
    label: "Not Started", 
    description: "Awaiting validation",
    color: "bg-gray-100 text-gray-600 border-gray-200",
  },
};

// Category configurations
const CATEGORY_CONFIG = {
  agency_config: { icon: Building, label: "Organization Configuration", color: "bg-purple-500" },
  client_ops: { icon: Heart, label: "Client Operations", color: "bg-pink-500" },
  caregiver_ops: { icon: User, label: "Team Operations", color: "bg-blue-500" },
  scheduling: { icon: Calendar, label: "Scheduling", color: "bg-indigo-500" },
  financial: { icon: DollarSign, label: "Financial Operations", color: "bg-emerald-500" },
  ai_ops: { icon: Sparkles, label: "AI Operations", color: "bg-amber-500" },
};

// Auto status icons
const AUTO_STATUS_ICON = {
  passed: <CheckCircle className="w-4 h-4 text-emerald-500" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
  partial: <AlertCircle className="w-4 h-4 text-amber-500" />,
  not_checked: <Clock className="w-4 h-4 text-gray-400" />,
};

// Validation checkpoint card
const CheckpointCard = ({ checkpoint, onSignOff }) => {
  const [expanded, setExpanded] = useState(false);
  const [signing, setSigning] = useState(false);
  const [notes, setNotes] = useState("");
  
  const confidenceConfig = CONFIDENCE_CONFIG[checkpoint.confidence] || CONFIDENCE_CONFIG.not_started;
  const isCertified = checkpoint.is_certified;
  
  const handleSignOff = async () => {
    setSigning(true);
    try {
      await onSignOff(checkpoint.id, notes);
      setNotes("");
    } catch (err) {
      toast.error("Failed to sign off");
    } finally {
      setSigning(false);
    }
  };
  
  return (
    <div className={`bg-white rounded-lg border ${isCertified ? 'border-emerald-200' : 'border-gray-200'} overflow-hidden`}>
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">{confidenceConfig.icon}</span>
          <div>
            <h4 className="font-medium text-gray-900">{checkpoint.name}</h4>
            <p className="text-sm text-gray-500">{checkpoint.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Auto validation status */}
          <div className="flex items-center gap-1">
            {AUTO_STATUS_ICON[checkpoint.auto_status]}
            <span className="text-xs text-gray-500 capitalize">{checkpoint.auto_status?.replace('_', ' ')}</span>
          </div>
          
          {/* Manual approval status */}
          {checkpoint.manual_approved ? (
            <Badge className="bg-emerald-100 text-emerald-700">
              <Check className="w-3 h-3 mr-1" />
              Signed Off
            </Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-600">Pending</Badge>
          )}
          
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          {/* Auto validation details */}
          {checkpoint.auto_details && checkpoint.auto_details.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Automated Validation</h5>
              <div className="bg-gray-50 rounded-lg p-3">
                {checkpoint.auto_details.map((detail, idx) => (
                  <p key={idx} className="text-sm text-gray-600">{detail}</p>
                ))}
              </div>
            </div>
          )}
          
          {/* Manual sign-off */}
          {checkpoint.manual_approved ? (
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-sm text-emerald-700">
                <strong>Signed off by:</strong> {checkpoint.manual_reviewer_name || 'Unknown'}
              </p>
              {checkpoint.manual_approved_at && (
                <p className="text-sm text-emerald-600">
                  {new Date(checkpoint.manual_approved_at).toLocaleString()}
                </p>
              )}
              {checkpoint.manual_notes && (
                <p className="text-sm text-emerald-600 mt-1">
                  <strong>Notes:</strong> {checkpoint.manual_notes}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional: Add notes about this certification..."
                className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#06372B] focus:border-transparent"
                rows={2}
              />
              <Button
                onClick={handleSignOff}
                disabled={signing || checkpoint.auto_status === 'failed'}
                className="bg-[#06372B] hover:bg-[#0a4a3a]"
              >
                {signing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Sign Off & Certify
              </Button>
              {checkpoint.auto_status === 'failed' && (
                <p className="text-sm text-red-600">
                  Cannot sign off until automated validation passes
                </p>
              )}
            </div>
          )}
          
          {/* Usage count */}
          {checkpoint.usage_count > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Used {checkpoint.usage_count} time(s) in real operations
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Category section
const CategorySection = ({ categoryKey, category, onSignOff }) => {
  const [expanded, setExpanded] = useState(true);
  const config = CATEGORY_CONFIG[categoryKey] || { icon: Shield, label: categoryKey, color: "bg-gray-500" };
  const Icon = config.icon;
  
  return (
    <div className="mb-6">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{category.name}</h3>
            <p className="text-sm text-gray-500">
              {category.passed} of {category.total} certified
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32">
            <Progress value={category.percentage} className="h-2" />
          </div>
          <span className="text-lg font-bold text-gray-900">{category.percentage}%</span>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>
      
      {expanded && (
        <div className="mt-3 space-y-2 ml-4">
          {category.checkpoints.map((cp) => (
            <CheckpointCard 
              key={cp.id} 
              checkpoint={cp} 
              onSignOff={onSignOff}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
export default function OperationalValidation() {
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState(null);
  
  useEffect(() => {
    loadValidation();
  }, []);
  
  const loadValidation = async () => {
    setLoading(true);
    try {
      const res = await api.get("/operations/validation");
      setValidation(res.data);
    } catch (err) {
      console.error("Failed to load validation:", err);
      toast.error("Failed to load validation dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSignOff = async (checkpointId, notes) => {
    try {
      await api.post(`/operations/validation/${checkpointId}/sign-off`, { notes });
      toast.success("Checkpoint certified");
      loadValidation();
    } catch (err) {
      throw err;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }
  
  const { 
    readiness_percentage = 0, 
    is_ready_for_pilot,
    ready_status,
    remaining_items = [],
    confidence_summary = {},
    by_category = {},
  } = validation || {};
  
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="operational-validation">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Operational Validation</h1>
              <p className="text-gray-500">
                Certify that A Touch of Angels can operate inside Leynix
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={loadValidation}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Readiness Card */}
      <div className={`rounded-2xl p-6 mb-8 ${is_ready_for_pilot ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-amber-50 border-2 border-amber-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Operational Readiness</h2>
            <p className="text-sm text-gray-600">
              {is_ready_for_pilot 
                ? "Ready to begin pilot operations"
                : "Complete remaining items before pilot"
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold text-gray-900">{readiness_percentage}%</p>
            <Badge className={is_ready_for_pilot ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}>
              {ready_status}
            </Badge>
          </div>
        </div>
        
        <Progress value={readiness_percentage} className="h-3 mb-4" />
        
        {/* Confidence Summary */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          {Object.entries(CONFIDENCE_CONFIG).map(([key, config]) => (
            <div key={key} className={`rounded-lg p-3 border ${config.color}`}>
              <div className="flex items-center gap-2 mb-1">
                <span>{config.icon}</span>
                <span className="font-medium text-sm">{config.label}</span>
              </div>
              <p className="text-2xl font-bold">{confidence_summary[key] || 0}</p>
            </div>
          ))}
        </div>
        
        {/* Remaining Items */}
        {remaining_items.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Remaining Items</h3>
            <ul className="space-y-1">
              {remaining_items.slice(0, 5).map((item, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                  <ArrowRight className="w-3 h-3" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Category Sections */}
      {Object.entries(by_category).map(([key, category]) => (
        <CategorySection 
          key={key}
          categoryKey={key}
          category={category}
          onSignOff={handleSignOff}
        />
      ))}
    </div>
  );
}
